// Receipts tile (companion) — the LEDGER on the Contacts chassis: search + camera +
import { confirmDiscard } from "./guard";
// "+ New" in the toolbar, the shared finance_documents list, and ONE full editor
// (every desktop field) reached three ways: photo→AI-parse (prefilled), "+ New"
// (blank) or a row tap (existing → PATCH). Same engine + ledger as the Outlook
// add-in via the S2S proxies in netlify/functions/finance.mts.
import { toast } from "./app";
import { loadSnap, saveSnap, skeletonHtml } from "./cache";
import { fileToCanvas } from "./capture";
import { downscaleToJpegB64 } from "./contacts";
import { detectAndExtract } from "./detect";
import { makeDropdown } from "./dropdown";
import { confirmDanger } from "./confirm";
import { fmtMoney as fmtMoneyLoc } from "./locale";

const $ = <T extends Element = HTMLElement>(sel: string): T | null => document.querySelector<T>(sel);
const esc = (s: unknown): string => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));

export type LedgerDoc = {
  id: string; kind: string;
  vendor_name?: string | null; vendor_vat?: string | null; vendor_email?: string | null;
  vendor_domain?: string | null; vendor_address?: string | null;
  doc_ref?: string | null; doc_date?: string | null; due_date?: string | null;
  currency?: string | null; amount_net?: number | null; amount_total?: number | null;
  vat_amount?: number | null; vat_rate?: number | null;
  payment_method?: string | null; paid?: boolean | null; paid_at?: string | null; source?: string | null;
  project_id?: string | null; project_name?: string | null;
  category_id?: string | null; category_name?: string | null;
  forwarded_at?: string | null; notes?: string | null; file_name?: string | null;
};
type NameRow = { id: string; name: string; colour?: string | null };
type PayRow = { id: string; label: string };
type Bundle = { documents: LedgerDoc[]; projects: NameRow[]; payment_methods: PayRow[]; categories: NameRow[] };

type Parsed = {
  kind?: string; vendor_name?: string; vendor_vat?: string; vendor_email?: string; vendor_domain?: string;
  vendor_address?: string; doc_ref?: string; doc_date?: string; due_date?: string; currency?: string;
  amount_net?: number | null; vat_amount?: number | null; amount_total?: number | null; vat_rate?: number | null;
  payment_method?: string;
};

let docs: LedgerDoc[] = [];
let projects: NameRow[] = [];
let categories: NameRow[] = [];
let payMethods: PayRow[] = [];
let ledgerLoaded = false; // a real fetch answered this session (snap-only ≠ loaded)

let editId: string | null = null;
let scanFile: File | null = null; // the captured original — re-encoded to a compressed grayscale (server wraps to PDF) at save
let kind: "invoice" | "receipt" = "receipt";
let paidTouched = false; // once the user flips "Already paid", switching the kind won't re-default it
let ddPay: { getValue: () => string } | null = null;
let ddProject: { getValue: () => string } | null = null;
let ddCategory: { getValue: () => string } | null = null;

const val = (id: string): string => ($(id) as HTMLInputElement | null)?.value.trim() || "";
const setVal = (id: string, v: string): void => { const el = $(id) as HTMLInputElement | null; if (el) el.value = v; };
const numStr = (n: number | null | undefined): string => (n == null ? "" : String(n));

function busy(on: boolean): void { const b = $("#rcBusy") as HTMLElement | null; if (b) b.hidden = !on; }

function fmtMoney(n: number, currency?: string | null): string {
  return fmtMoneyLoc(n, currency || "EUR"); // EU/US format synced from the account (see locale.ts)
}
function fmtAmount(d: LedgerDoc): string {
  return d.amount_total == null ? "" : fmtMoney(d.amount_total, d.currency || "EUR");
}

// ───────────────────────── list ─────────────────────────
function renderLedger(): void {
  const list = $("#rcList");
  if (!list) return;
  const term = ($("#rcSearch") as HTMLInputElement | null)?.value.trim().toLowerCase() || "";
  const clear = $("#rcClear") as HTMLElement | null;
  if (clear) clear.hidden = !term;

  // top-bar count chip — only while Receipts is the active screen (boot prefetch
  // renders in the background; see contacts.ts)
  const count = $("#topCount");
  if (count && $("#screen-receipts")?.classList.contains("active")) {
    count.textContent = docs.length ? String(docs.length) : "";
    count.hidden = !docs.length;
  }

  const shown = term
    ? docs.filter((d) =>
        [d.vendor_name, d.vendor_vat, d.doc_ref, d.notes, d.file_name, d.payment_method, d.project_name, d.category_name]
          .filter(Boolean).join(" ").toLowerCase().includes(term))
    : docs;

  // desktop parity: "{n} documents · {total}" (total only when one currency)
  const sum = $("#rcSum") as HTMLElement | null;
  if (sum) {
    if (!shown.length) sum.hidden = true;
    else {
      const curr = [...new Set(shown.map((d) => d.currency || "EUR"))];
      const total = shown.reduce((a, d) => a + (d.amount_total || 0), 0);
      sum.textContent = `${shown.length} document${shown.length === 1 ? "" : "s"}${curr.length === 1 ? ` · ${fmtMoney(total, curr[0])}` : ""}`;
      sum.hidden = false;
    }
  }

  if (!shown.length) {
    list.innerHTML = !term && !ledgerLoaded && !docs.length
      ? skeletonHtml(4)
      : `<div class="cpane-empty">${term ? "No documents match your search." : "No documents yet — photograph a receipt or file one with + New."}</div>`;
    return;
  }
  list.innerHTML = shown
    .map((d) => {
      const title = d.vendor_name || d.doc_ref || d.file_name || "Document";
      const sub = [
        d.kind === "invoice" ? "Invoice" : "Receipt",
        d.doc_date || "",
        d.doc_ref || "",
        d.project_name || "",
        d.category_name || "",
        d.forwarded_at ? "Sent" : "",
      ].filter(Boolean).join(" · ");
      return (
        `<button class="rc-row" data-id="${esc(d.id)}">` +
        `<span class="rc-rowic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 2v20l2-1.5L8 22l2-1.5L12 22l2-1.5L16 22l2-1.5L20 22V2l-2 1.5L16 2l-2 1.5L12 2l-2 1.5L8 2 6 3.5 4 2z"/><path d="M8 8h8M8 12h8M8 16h5"/></svg></span>` +
        `<span class="rc-rowbody"><span class="rc-rowtitle">${esc(title)}</span><span class="rc-rowsub">${esc(sub)}</span></span>` +
        `<span class="rc-rowamt">${esc(fmtAmount(d))}</span>` +
        `</button>`
      );
    })
    .join("");
}

/** Stale-while-revalidate (house pattern): the snapshot paints instantly, the S2S GET
 *  refreshes documents + the editor's dropdown sources. */
export async function loadLedger(force = false): Promise<void> {
  if (!docs.length) {
    const snap = loadSnap<Bundle | LedgerDoc[]>("receipts");
    if (snap) {
      // tolerate the round-1 array-shaped snapshot
      if (Array.isArray(snap)) docs = snap;
      else { docs = snap.documents || []; projects = snap.projects || []; categories = snap.categories || []; payMethods = snap.payment_methods || []; }
      renderLedger();
    }
  }
  if (ledgerLoaded && !force) { renderLedger(); return; }
  renderLedger(); // skeleton on a true first run
  try {
    const r = await fetch("/api/receipts", { credentials: "include" });
    if (r.status === 402) {
      ledgerLoaded = true;
      const list = $("#rcList");
      if (list && !docs.length) list.innerHTML = `<div class="cpane-empty">Receipts are part of the subscription.</div>`;
      return;
    }
    if (!r.ok) return; // keep whatever is painted
    const j = (await r.json()) as Partial<Bundle>;
    docs = (j.documents || []).map((d) => ({ ...d, id: String(d.id) }));
    projects = j.projects || [];
    categories = j.categories || [];
    payMethods = j.payment_methods || [];
    ledgerLoaded = true;
    saveSnap("receipts", { documents: docs, projects, payment_methods: payMethods, categories });
    renderLedger();
  } catch { /* offline — the painted snapshot stands */ }
}

// ───────────────────────── editor ─────────────────────────
function syncKind(): void {
  document.querySelectorAll<HTMLElement>("#rcfKind [data-k]").forEach((b) => b.classList.toggle("on", b.getAttribute("data-k") === kind));
}
// Show the "Paid on" date only while "Already paid" is ticked.
function syncPaid(): void {
  const chk = $("#rcfPaid") as HTMLInputElement | null;
  const wrap = $("#rcfPaidAtWrap") as HTMLElement | null;
  if (wrap) wrap.style.display = chk?.checked ? "flex" : "none"; // "flex" = the .rp-field default (block broke its column layout)
}

function mountDropdowns(d: LedgerDoc | null, parsed: Parsed | null): void {
  const payHost = $("#rcfPay"), prHost = $("#rcfProject"), caHost = $("#rcfCategory");
  if (payHost) {
    payHost.innerHTML = "";
    const current = d?.payment_method || parsed?.payment_method || "";
    const opts = payMethods.map((m) => ({ value: m.label, label: m.label }));
    if (current && !opts.some((o) => o.value === current)) opts.unshift({ value: current, label: current });
    const dd = makeDropdown({ placeholder: "— none —", value: current, searchable: opts.length > 5, options: [{ value: "", label: "— none —" }, ...opts] });
    payHost.appendChild(dd.el); ddPay = dd;
  }
  if (prHost) {
    prHost.innerHTML = "";
    const dd = makeDropdown({
      placeholder: "— none —", value: d?.project_id || "", searchable: projects.length > 5,
      options: [{ value: "", label: "— none —" }, ...projects.map((p) => ({ value: p.id, label: p.name }))],
    });
    prHost.appendChild(dd.el); ddProject = dd;
  }
  if (caHost) {
    caHost.innerHTML = "";
    const dd = makeDropdown({
      placeholder: "— none —", value: d?.category_id || "", searchable: categories.length > 5,
      options: [{ value: "", label: "— none —" }, ...categories.map((c) => ({ value: c.id, label: c.name }))],
    });
    caHost.appendChild(dd.el); ddCategory = dd;
  }
}

function openEditor(d: LedgerDoc | null, parsed: Parsed | null = null): void {
  editId = d ? String(d.id) : null;
  scanFile = null; // cleared on every open; the scan flow re-sets it after this call
  kind = (d?.kind || parsed?.kind) === "invoice" ? "invoice" : "receipt";
  syncKind();

  const title = $("#rcEdTitle"); if (title) title.textContent = d ? "Document" : "New document";
  const del = $("#rcEdDelete") as HTMLElement | null; if (del) del.hidden = !d;
  const mail = $("#rcEdMail") as HTMLElement | null; if (mail) mail.hidden = !d; // existing doc (the server attaches the file when there is one)

  setVal("#rcfVendor", d?.vendor_name || parsed?.vendor_name || "");
  setVal("#rcfVendorVat", d?.vendor_vat || parsed?.vendor_vat || "");
  setVal("#rcfVendorDomain", d?.vendor_domain || parsed?.vendor_domain || "");
  setVal("#rcfVendorEmail", d?.vendor_email || parsed?.vendor_email || "");
  setVal("#rcfVendorAddr", d?.vendor_address || parsed?.vendor_address || "");
  setVal("#rcfDate", (d?.doc_date || parsed?.doc_date || "").slice(0, 10));
  setVal("#rcfDue", (d?.due_date || parsed?.due_date || "").slice(0, 10));
  setVal("#rcfRef", d?.doc_ref || parsed?.doc_ref || "");
  setVal("#rcfCurrency", d?.currency || parsed?.currency || "EUR");
  setVal("#rcfNet", numStr(d?.amount_net ?? parsed?.amount_net));
  setVal("#rcfVat", numStr(d?.vat_amount ?? parsed?.vat_amount));
  setVal("#rcfVatRate", numStr(d?.vat_rate ?? parsed?.vat_rate));
  setVal("#rcfTotal", numStr(d?.amount_total ?? parsed?.amount_total));
  const notes = $("#rcfNotes") as HTMLTextAreaElement | null; if (notes) notes.value = d?.notes || "";
  // Paid status — existing doc keeps its value; a NEW receipt defaults to paid (dated the receipt),
  // a new invoice defaults to unpaid. paidTouched preserves a manual choice across a kind switch.
  paidTouched = false;
  const paidVal = d ? !!d.paid : kind === "receipt";
  const paidChk = $("#rcfPaid") as HTMLInputElement | null;
  if (paidChk) paidChk.checked = paidVal;
  setVal("#rcfPaidAt", (d?.paid_at ? String(d.paid_at) : (paidVal ? (d?.doc_date || parsed?.doc_date || "") : "")).slice(0, 10));
  syncPaid();
  setDict("idle");
  mountDropdowns(d, parsed);

  // read-only context: where the document came from + the stored file's name
  const ctx = $("#rcEdCtx");
  if (ctx) {
    ctx.innerHTML =
      (d?.source ? `<div class="tk-ctx-row"><span class="tk-ctx-k">Source</span><span class="tk-ctx-v">${esc(d.source)}</span></div>` : "") +
      (d?.file_name ? `<div class="tk-ctx-row"><span class="tk-ctx-k">Original</span><span class="tk-ctx-v"><button type="button" id="rcViewOrig" style="background:none;border:0;padding:0;color:var(--accent,#2dd1a7);font:inherit;text-decoration:underline;cursor:pointer">${esc(d.file_name)}</button></span></div>` : "") +
      (d?.file_name ? `<div class="tk-ctx-row"><span class="tk-ctx-k">Bookkeeping</span><span class="tk-ctx-v">${d.forwarded_at ? "Sent · " : ""}<button type="button" id="rcFwd" style="background:none;border:0;color:var(--accent,#2dd1a7);font:inherit;text-decoration:underline;cursor:pointer;padding:0">${d.forwarded_at ? "Resend" : "Send to bookkeeper"}</button></span></div>` : "");
    (ctx as HTMLElement).style.display = ctx.innerHTML ? "flex" : "none";
    const vo = $("#rcViewOrig") as HTMLButtonElement | null;
    if (vo && d) vo.onclick = () => void viewOriginal(String(d.id));
    const fwd = $("#rcFwd") as HTMLButtonElement | null;
    if (fwd && d) fwd.onclick = () => void forwardDoc(String(d.id));
  }

  // New scan → offer to also forward to the bookkeeper on save (existing docs use the ctx button above).
  const fwdOpt = $("#rcFwdOpt") as HTMLElement | null;
  if (fwdOpt) fwdOpt.style.display = d ? "none" : "flex";
  const fwdChk = $("#rcFwdChk") as HTMLInputElement | null;
  if (fwdChk) fwdChk.checked = false;

  $("#rcEditor")?.classList.add("open");
  setTimeout(() => { rcOpenSnap = JSON.stringify(fields()); }, 0);
}
function closeEditor(): void {
  try { if (dictMedia && dictMedia.state !== "inactive") dictMedia.stop(); } catch { /* ignore */ }
  dictStream?.getTracks().forEach((t) => t.stop()); dictStream = null;
  $("#rcEditor")?.classList.remove("open");
  editId = null;
  scanFile = null;
}

/** Home-search result tap → straight into the editor (after loadLedger's snapshot paint). */
export function openLedgerDoc(id: string): void {
  const d = docs.find((x) => String(x.id) === id);
  if (d) openEditor(d);
}

function fields(): Record<string, unknown> {
  return {
    kind,
    vendor_name: val("#rcfVendor"),
    vendor_vat: val("#rcfVendorVat"),
    vendor_domain: val("#rcfVendorDomain"),
    vendor_email: val("#rcfVendorEmail"),
    vendor_address: val("#rcfVendorAddr"),
    doc_date: val("#rcfDate"),
    due_date: val("#rcfDue"),
    doc_ref: val("#rcfRef"),
    currency: val("#rcfCurrency") || "EUR",
    amount_net: val("#rcfNet"),
    vat_amount: val("#rcfVat"),
    vat_rate: val("#rcfVatRate"),
    amount_total: val("#rcfTotal"),
    payment_method: ddPay?.getValue() || "",
    paid: (($("#rcfPaid") as HTMLInputElement | null)?.checked) || false,
    paid_at: (($("#rcfPaid") as HTMLInputElement | null)?.checked) ? val("#rcfPaidAt") : "",
    project_id: ddProject?.getValue() || null,
    category_id: ddCategory?.getValue() || null,
    notes: (($("#rcfNotes") as HTMLTextAreaElement | null)?.value || "").trim(),
  };
}

// SAVE-CANON C2/C4: back on an EXISTING doc auto-saves the edits; back on a NEW (scanned/typed)
// one asks the shared Discard confirm — the scan would be lost.
let rcOpenSnap = "";
async function flushCloseReceipts(): Promise<void> {
  const f = fields();
  if (editId) {
    if (JSON.stringify(f) !== rcOpenSnap) {
      try {
        const r = await fetch(`/api/receipts/${encodeURIComponent(editId)}`, {
          method: "PATCH", headers: { "content-type": "application/json" }, credentials: "include",
          body: JSON.stringify(f),
        });
        if (!r.ok) { toast("Couldn't save — try again"); return; } // stay open
        void loadLedger(true);
      } catch { toast("Couldn't save — check your connection"); return; }
    }
  } else if (scanFile || Object.values(f).some((v) => typeof v === "string" && v.trim() !== "" && v !== "EUR")) {
    if (!(await confirmDiscard("this document"))) return;
  }
  closeEditor();
}

async function save(): Promise<void> {
  const body = fields();
  if (!body.vendor_name) { toast("Add the supplier name"); return; }
  const btn = $("#rcSave") as HTMLButtonElement | null;
  if (btn) { btn.disabled = true; btn.textContent = "Saving…"; }
  try {
    // New scan → archive a compressed GRAYSCALE copy of the original (the server wraps it into a PDF).
    // OCR already ran on the sharper colour image; this smaller grayscale one is purely for the record.
    const storeB64 = !editId && scanFile ? await downscaleToJpegB64(scanFile, 1600, 0.6, true).catch(() => null) : null;
    const r = editId
      ? await fetch(`/api/receipts/${encodeURIComponent(editId)}`, {
          method: "PATCH", headers: { "content-type": "application/json" }, credentials: "include",
          body: JSON.stringify(body),
        })
      : await fetch("/api/receipts/save", {
          method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
          body: JSON.stringify(storeB64 ? { ...body, image_b64: storeB64 } : body),
        });
    const j = (await r.json().catch(() => ({}))) as { ok?: boolean; document?: unknown; error?: string; id?: unknown; storage_full?: boolean };
    if (r.status === 402) { toast("Receipts are part of the subscription"); return; }
    if (!r.ok || (!j.ok && !j.document)) { toast("Couldn't save — try again"); return; }
    const alsoForward = !editId && (($("#rcFwdChk") as HTMLInputElement | null)?.checked ?? false);
    const newId = j.id != null ? String(j.id) : null;
    closeEditor();
    // BACKLOG #009: a full storage cap is no longer silent — the row saved, the scan did NOT.
    if (j.storage_full) toast("Filed — but the scan was NOT stored: storage is full. Free up space in the desktop app.");
    else toast(editId ? "Saved" : "Filed to your ledger");
    void loadLedger(true);
    if (alsoForward && newId) void sendToBookkeeper(newId); // "also send to bookkeeper" opt-in (#005a)
  } catch { toast("Couldn't save — check your connection"); }
  finally {
    if (btn) { btn.disabled = false; btn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg> Save`; }
  }
}

async function removeDoc(): Promise<void> {
  if (!editId) return;
  const d = docs.find((x) => String(x.id) === editId);
  const label = d?.vendor_name || d?.doc_ref || "this document";
  const yes = await confirmDanger({
    title: "Delete this document?",
    body: `"${label}" disappears from your ledger on every device. This cannot be undone.`,
    cta: "Delete document",
  });
  if (!yes) return;
  try {
    const r = await fetch(`/api/receipts/${encodeURIComponent(editId)}`, { method: "DELETE", credentials: "include" });
    if (!r.ok) { toast("Couldn't delete — try again"); return; }
    closeEditor();
    toast("Document deleted");
    void loadLedger(true);
  } catch { toast("Couldn't delete — check your connection"); }
}

// Open the stored original (a compressed grayscale PDF) via a short-lived presigned URL.
async function viewOriginal(id: string): Promise<void> {
  const w = window.open("", "_blank"); // open inside the click gesture so Safari doesn't block it
  try {
    const r = await fetch("/api/receipts/file", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify({ id }),
    });
    const j = (await r.json().catch(() => ({}))) as { url?: string; error?: string };
    if (!r.ok || !j.url) { w?.close(); toast("Couldn't open the original"); return; }
    if (w) w.location.href = j.url; else location.href = j.url;
  } catch { w?.close(); toast("Couldn't open — check your connection"); }
}

// Forward a filed receipt to the user's bookkeeping address (#005a) — shared by the detail button
// AND the "also send" opt-in on a fresh save.
async function sendToBookkeeper(id: string): Promise<void> {
  try {
    const r = await fetch("/api/receipts/forward", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify({ id }),
    });
    const j = (await r.json().catch(() => ({}))) as { error?: string; to?: string };
    if (r.ok) { toast(`Sent to ${j.to || "bookkeeping"}`); void loadLedger(true); }
    else if (j.error === "no_bookkeeping_email") toast("Set a bookkeeping email in Settings (on desktop) first");
    else if (j.error === "no_stored_file") toast("No stored file to forward");
    else toast("Couldn't send — try again");
  } catch { toast("Couldn't send — check your connection"); }
}
async function forwardDoc(id: string): Promise<void> {
  const btn = $("#rcFwd") as HTMLButtonElement | null;
  if (btn) btn.disabled = true;
  try { await sendToBookkeeper(id); } finally { if (btn) btn.disabled = false; }
}
// "Email to me" — mail the filed receipt (original file attached) to the owner's own address.
async function mailToMe(id: string): Promise<void> {
  const btn = $("#rcEdMail") as HTMLButtonElement | null;
  const label = btn?.innerHTML;
  if (btn) { btn.disabled = true; btn.textContent = "Sending…"; }
  try {
    const r = await fetch("/api/receipts/mail", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify({ id }),
    });
    const j = (await r.json().catch(() => ({}))) as { ok?: boolean; to?: string; error?: string };
    if (r.ok && j.ok) toast(`Sent to ${j.to || "you"}`);
    else if (j.error === "no_stored_file") toast("No stored file to email");
    else if (j.error === "no_recipient") toast("No address on your account");
    else toast("Couldn't send — try again");
  } catch { toast("Couldn't send — check your connection"); }
  finally { if (btn) { btn.disabled = false; if (label) btn.innerHTML = label; } }
}

// ───────────────────────── capture → parse → editor ─────────────────────────
// C360 "+ Receipt" (spec §8a, phone port): the fiche arms the vendor identity for the NEXT capture
// only — the prefill wins over the parse for those fields (both stay visible + editable in the
// editor, propose→confirm), and the seed clears on use or when the user leaves the screen, so a
// later unrelated scan never silently inherits the contact's vendor.
let armedVendor: Partial<Parsed> | null = null;
export function captureReceiptFor(v: { vendor_name?: string; vendor_email?: string; vendor_domain?: string }): void {
  armedVendor = { ...v };
  ($("#rcFileInput") as HTMLInputElement | null)?.click();
}
export function clearArmedVendor(): void { armedVendor = null; }
async function onCaptured(input: HTMLInputElement): Promise<void> {
  const file = input.files?.[0];
  input.value = "";
  const armed = armedVendor; armedVendor = null; // one capture only
  if (!file) return;
  busy(true);
  try {
    // Same on-device Scanic crop/deskew as the Scanner, so a receipt photographed on a table goes
    // into the parse AND the archive as just the document. Best-effort: any failure (init, low
    // confidence, over-crop guard) falls back to the original photo.
    let src = file;
    try {
      const cropped = await detectAndExtract(await fileToCanvas(file, 2000)); // 2000: receipts keep more detail for the parse
      const blob = cropped && (await new Promise<Blob | null>((res) => cropped.toBlob(res, "image/jpeg", 0.92)));
      if (blob) src = new File([blob], file.name || "receipt.jpg", { type: "image/jpeg" });
    } catch { /* keep the original */ }
    const b64 = await downscaleToJpegB64(src, 2000, 0.88); // receipts need more detail than cards
    const r = await fetch("/api/receipts/parse", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify({ image_b64: b64, filename: src.name || "receipt.jpg", content_type: "image/jpeg" }),
    });
    const j = (await r.json().catch(() => ({}))) as { parsed?: Parsed; error?: string };
    busy(false);
    if (r.status === 402) { toast("Receipts are part of the subscription"); return; }
    if (!r.ok) { toast(j.error === "image_too_large" ? "Photo too large — try again" : "Couldn't read the receipt — try again"); return; }
    openEditor(null, { ...(j.parsed || {}), ...(armed || {}) } as Parsed);
    scanFile = src; // keep the (cropped) capture for /save → compressed grayscale archive
  } catch {
    busy(false);
    toast("Couldn't read the receipt — check your connection");
  }
}

// ───────────────────────── dictation (notes) — same pattern as Minutes ─────────────────────────
let dictMedia: MediaRecorder | null = null;
let dictStream: MediaStream | null = null;
let dictChunks: Blob[] = [];
let dictFmt = "webm";
function pickMime(): { mime: string; f: string } {
  const cands = [
    { mime: "audio/mp4", f: "m4a" }, // iOS Safari
    { mime: "audio/webm;codecs=opus", f: "webm" },
    { mime: "audio/webm", f: "webm" },
  ];
  for (const c of cands) if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported?.(c.mime)) return c;
  return { mime: "", f: "webm" };
}
function setDict(s: "idle" | "rec" | "busy"): void { $("#rcDict")?.setAttribute("data-dict", s); }
async function toggleDict(): Promise<void> {
  const s = $("#rcDict")?.getAttribute("data-dict") || "idle";
  if (s === "busy") return;
  if (s === "rec") { try { dictMedia?.stop(); } catch { /* onstop */ } return; }
  try { dictStream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
  catch { toast("We couldn't access your microphone"); return; }
  const { mime, f } = pickMime();
  dictFmt = f; dictChunks = [];
  try { dictMedia = mime ? new MediaRecorder(dictStream, { mimeType: mime }) : new MediaRecorder(dictStream); }
  catch { dictMedia = new MediaRecorder(dictStream!); }
  dictMedia.ondataavailable = (ev) => { if (ev.data?.size) dictChunks.push(ev.data); };
  dictMedia.onstop = () => void dictFinish();
  dictMedia.start();
  setDict("rec");
}
async function dictFinish(): Promise<void> {
  const blob = new Blob(dictChunks);
  dictChunks = [];
  dictStream?.getTracks().forEach((t) => t.stop()); dictStream = null; dictMedia = null;
  if (blob.size < 1200) { setDict("idle"); return; }
  setDict("busy");
  try {
    const b64 = await new Promise<string>((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => { const s = String(fr.result || ""); res(s.slice(s.indexOf(",") + 1)); };
      fr.onerror = () => rej(new Error("read_failed"));
      fr.readAsDataURL(blob);
    });
    const r = await fetch("/api/dictate", {
      method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
      body: JSON.stringify({ audio_b64: b64, audio_format: dictFmt }),
    });
    const j = (await r.json().catch(() => ({}))) as { text?: string; error?: string };
    if (j.text) {
      const ta = $("#rcfNotes") as HTMLTextAreaElement | null;
      if (ta) ta.value = (ta.value ? ta.value.trimEnd() + "\n" : "") + j.text;
    } else if (j.error === "no_speech") toast("We couldn't hear any speech — try again");
    else toast("Dictation failed — try again");
  } catch { toast("Dictation failed — check your connection"); }
  finally { setDict("idle"); }
}

// ───────────────────────── wiring ─────────────────────────
export function initReceipts(): void {
  // toolbar: camera (iOS shows Take Photo / Library in one sheet — input has no capture attr)
  $("#rcShoot")?.addEventListener("click", () => ($("#rcFileInput") as HTMLInputElement | null)?.click());
  $("#rcFileInput")?.addEventListener("change", (e) => void onCaptured(e.target as HTMLInputElement));
  // C360 "+ Receipt": a cancelled camera sheet leaves the armed vendor behind — drop it as soon
  // as the user navigates elsewhere, so it can never ride a later unrelated scan.
  document.addEventListener("rp:screen", (e) => { if ((e as CustomEvent).detail !== "receipts") clearArmedVendor(); });
  $("#rcNew")?.addEventListener("click", () => openEditor(null));

  // list
  const search = $("#rcSearch") as HTMLInputElement | null;
  search?.addEventListener("input", renderLedger);
  $("#rcClear")?.addEventListener("click", () => { if (search) search.value = ""; renderLedger(); search?.focus(); });
  $("#rcList")?.addEventListener("click", (e) => {
    const row = (e.target as HTMLElement).closest<HTMLElement>("[data-id]");
    if (row) { const d = docs.find((x) => String(x.id) === row.getAttribute("data-id")); if (d) openEditor(d); }
  });

  // editor
  $("#rcEdBack")?.addEventListener("click", () => void flushCloseReceipts());
  $("#rcSave")?.addEventListener("click", () => void save());
  $("#rcEdDelete")?.addEventListener("click", () => void removeDoc());
  $("#rcEdMail")?.addEventListener("click", () => { if (editId) void mailToMe(editId); });
  $("#rcDict")?.addEventListener("click", () => void toggleDict());
  $("#rcfKind")?.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-k]");
    if (!b) return;
    kind = b.getAttribute("data-k") === "invoice" ? "invoice" : "receipt";
    syncKind();
    // Mirror the desktop default when the kind is corrected — until the user touches Paid themselves.
    if (!paidTouched) {
      const chk = $("#rcfPaid") as HTMLInputElement | null;
      if (chk) { chk.checked = kind === "receipt"; if (chk.checked && !val("#rcfPaidAt")) setVal("#rcfPaidAt", val("#rcfDate")); syncPaid(); }
    }
  });
  $("#rcfPaid")?.addEventListener("change", () => {
    paidTouched = true;
    const chk = $("#rcfPaid") as HTMLInputElement | null;
    if (chk?.checked && !val("#rcfPaidAt")) setVal("#rcfPaidAt", val("#rcfDate")); // default to the doc date
    syncPaid();
  });
}
